<div class="min-h-screen bg-gray-100 py-8 px-4">
  <div class="max-w-4xl mx-auto">
    <!-- Header -->
    <div class="mb-6 flex items-center justify-between">
      <h1 class="text-2xl font-bold text-gray-900">BOQ Bidding Invitation</h1>
      <a *ngIf="!isAuthenticated" routerLink="/login" class="text-primary hover:underline">
        Log in
      </a>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="bg-white rounded-lg shadow p-8 text-center">
      <mat-spinner diameter="40" class="mx-auto"></mat-spinner>
      <p class="mt-4 text-gray-600">Loading invitation...</p>
    </div>

    <!-- Error -->
    <div *ngIf="!loading && error" class="bg-white rounded-lg shadow p-8 text-center">
      <mat-icon class="text-red-500 text-6xl">error_outline</mat-icon>
      <h2 class="text-xl font-semibold text-gray-900 mt-4">Invitation Not Available</h2>
      <p class="text-gray-600 mt-2">{{ error }}</p>
      <a routerLink="/login" mat-stroked-button color="primary" class="mt-4">Go to Login</a>
    </div>

    <!-- Content -->
    <div *ngIf="!loading && !error && data" class="space-y-6">
      <!-- Bid Closed Banner -->
      <mat-card *ngIf="bidClosed" class="p-6 bg-red-50 border-2 border-red-200">
        <div class="flex items-center gap-3 text-red-800">
          <mat-icon class="text-5xl w-12 h-12">block</mat-icon>
          <div class="flex-1">
            <h2 class="text-xl font-bold">Bid Closed</h2>
            <p class="text-red-700 mt-1">Bidding for this invitation has closed. No further submissions are accepted.</p>
            <p *ngIf="data.invitation?.bid_closing_at" class="text-sm text-red-600 mt-2">
              Closed on: {{ data.invitation.bid_closing_at | date: 'medium' }}
            </p>
            <div *ngIf="data.invitation?.closing_comments" class="mt-4 p-4 bg-white/60 rounded border border-red-200">
              <p class="text-xs font-semibold text-red-800 uppercase tracking-wide">Message from project planner</p>
              <p class="text-red-900 mt-1 whitespace-pre-wrap">{{ data.invitation.closing_comments }}</p>
            </div>
          </div>
        </div>
      </mat-card>

      <!-- Bid Closing Date (when still open) -->
      <mat-card *ngIf="!bidClosed && data.invitation?.bid_closing_at" class="p-4 bg-amber-50 border border-amber-200">
        <p class="text-sm text-amber-800">
          <mat-icon class="align-middle mr-1" style="font-size:18px;width:18px;height:18px">schedule</mat-icon>
          <strong>Bids close:</strong> {{ data.invitation.bid_closing_at | date: 'medium' }}
        </p>
      </mat-card>

      <!-- Project & BOQ Summary -->
      <mat-card class="p-6">
        <h2 class="text-lg font-semibold mb-4">Project & BOQ Summary</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4" *ngIf="data.project">
          <div>
            <span class="text-gray-600">Project:</span>
            <span class="font-medium ml-2">{{ data.project.project_name }}</span>
          </div>
          <div>
            <span class="text-gray-600">BOQ:</span>
            <span class="font-medium ml-2">{{ data.invitation.boq_code }} - {{ data.invitation.boq_name }}</span>
          </div>
          <div *ngIf="data.project.area">
            <span class="text-gray-600">Area:</span>
            <span class="font-medium ml-2">{{ data.project.area }}</span>
          </div>
          <div *ngIf="data.project.size">
            <span class="text-gray-600">Size:</span>
            <span class="font-medium ml-2">{{ data.project.size }}</span>
          </div>
          <div *ngIf="data.project.scope_description">
            <span class="text-gray-600">Scope:</span>
            <p class="mt-1 text-sm">{{ data.project.scope_description }}</p>
          </div>
        </div>
      </mat-card>

      <mat-card class="p-6" *ngIf="data.invitation?.fees as fees">
        <h2 class="text-lg font-semibold mb-2">Participation fee</h2>
        <p class="text-sm text-gray-500 mb-3">Display only — not collected on this portal.</p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span class="text-gray-600">BOQ total:</span>
            <span class="font-medium ml-2">{{ fees.currency || data.invitation.currency || 'INR' }} {{ fees.boq_total | number:'1.0-0' }}</span>
          </div>
          <div>
            <span class="text-gray-600">Bid fee ({{ fees.rate_pct }}%):</span>
            <span class="font-medium ml-2">{{ fees.currency || 'INR' }} {{ fees.bid_fee | number:'1.0-0' }}</span>
          </div>
        </div>
      </mat-card>

      <!-- Planner Requirements -->
      <mat-card class="p-6" *ngIf="!bidClosed && data.invitation?.requirements">
        <h2 class="text-lg font-semibold mb-2">Requirements / Instructions</h2>
        <p class="text-gray-700 whitespace-pre-wrap">{{ data.invitation.requirements }}</p>
      </mat-card>

      <!-- BOQ Items (editable qty and unit rate for authenticated bidders; amount = qty × rate) -->
      <mat-card class="p-6" *ngIf="!bidClosed">
        <h2 class="text-lg font-semibold mb-4">Bill of Quantities</h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b">
                <th class="text-left py-2">Item</th>
                <th class="text-left py-2">Description</th>
                <th class="text-left py-2">Unit</th>
                <th class="text-right py-2">Your Qty</th>
                <th class="text-right py-2">Your Rate</th>
                <th class="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              <ng-container *ngFor="let item of bidItems.length ? bidItems : data.boq_items">
                <tr class="border-b hover:bg-gray-50">
                  <td class="py-2">{{ item.item_code }}</td>
                  <td class="py-2">{{ item.item_description }}</td>
                  <td class="py-2">{{ item.unit }}</td>
                  <td class="py-2 text-right">
                    <input *ngIf="canEditBid() && useItemWiseBid && bidItems.length"
                      type="number" [(ngModel)]="item.quantity" (ngModelChange)="onBidQtyChange(item)"
                      min="0" step="0.01" class="w-20 text-right border rounded px-2 py-1"
                      placeholder="—">
                    <span *ngIf="!canEditBid() || !useItemWiseBid || !bidItems.length">{{ bidItems.length && (item.quantity != null && item.quantity !== '') ? (item.quantity | number:'1.2-2') : '—' }}</span>
                  </td>
                  <td class="py-2 text-right">
                    <input *ngIf="canEditBid() && useItemWiseBid && bidItems.length"
                      type="number" [(ngModel)]="item.bid_rate" (ngModelChange)="onBidRateChange(item)"
                      min="0" step="0.01" class="w-24 text-right border rounded px-2 py-1"
                      placeholder="—">
                    <span *ngIf="!canEditBid() || !useItemWiseBid || !bidItems.length">{{ bidItems.length && item.bid_rate != null ? formatCurrency(item.bid_rate) : '—' }}</span>
                  </td>
                  <td class="py-2 text-right font-medium">
                    {{ formatCurrency(item.line_total ?? (item.quantity * (item.bid_rate || item.unit_rate))) }}
                  </td>
                </tr>
                <!-- Optional bidder resources (expandable) -->
                <tr *ngIf="bidItems.length && item.item_id" class="bg-gray-50">
                  <td [attr.colspan]="6" class="py-3 px-4">
                    <ng-container *ngIf="canEditBid() && useItemWiseBid">
                      <button type="button" class="text-sm text-primary hover:underline flex items-center gap-1"
                        (click)="toggleResourceExpand(item.item_id)">
                        <mat-icon class="text-base">{{ isResourceExpanded(item.item_id) ? 'expand_less' : 'expand_more' }}</mat-icon>
                        Your resources (optional) — {{ (item.resources || []).length }} added
                      </button>
                      <div *ngIf="isResourceExpanded(item.item_id)" class="mt-3 pl-6">
                        <div class="resources-wrapper overflow-x-auto rounded-lg border border-gray-200 bg-white">
                          <table class="resources-table w-full text-sm">
                            <thead>
                              <tr class="border-b border-gray-200 bg-gray-50">
                                <th class="text-left py-2 px-2 font-medium text-gray-600">Type</th>
                                <th class="text-left py-2 px-2 font-medium text-gray-600">Description</th>
                                <th class="text-right py-2 px-2 font-medium text-gray-600 w-20">Qty</th>
                                <th class="text-left py-2 px-2 font-medium text-gray-600 w-20">Unit</th>
                                <th class="text-right py-2 px-2 font-medium text-gray-600 w-24">Rate</th>
                                <th class="text-center py-2 px-2 font-medium text-gray-600 w-20">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr *ngFor="let res of item.resources || []" class="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
                                <td class="py-2 px-2">
                                  <select [(ngModel)]="res.type" class="resources-input">
                                    <option value="man">Man</option>
                                    <option value="machine">Machine</option>
                                    <option value="material">Material</option>
                                  </select>
                                </td>
                                <td class="py-2 px-2">
                                  <input type="text" [(ngModel)]="res.description" placeholder="Description" class="resources-input min-w-[8rem]">
                                </td>
                                <td class="py-2 px-2 text-right">
                                  <input type="number" [(ngModel)]="res.quantity" min="0" step="0.001" placeholder="0" class="resources-input text-right w-16">
                                </td>
                                <td class="py-2 px-2">
                                  <input type="text" [(ngModel)]="res.unit" placeholder="Unit" class="resources-input w-16">
                                </td>
                                <td class="py-2 px-2 text-right">
                                  <input type="number" [(ngModel)]="res.unit_rate" min="0" step="0.01" placeholder="0" class="resources-input text-right w-20">
                                </td>
                                <td class="py-2 px-2 text-center">
                                  <button type="button" class="btn-remove-resource" matTooltip="Remove resource" (click)="removeBidderResource(item, res)">
                                    <mat-icon>delete_outline</mat-icon>
                                    <span class="btn-remove-label">Remove</span>
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <button type="button" mat-stroked-button class="mt-3 w-full sm:w-auto" (click)="addBidderResource(item)">
                          <mat-icon>add</mat-icon> Add resource
                        </button>
                      </div>
                    </ng-container>
                    <div *ngIf="(!canEditBid() || !useItemWiseBid) && (item.resources || []).length" class="text-sm text-gray-600">
                      Your resources: {{ getResourceDescriptions(item) }}
                    </div>
                  </td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>
        <p *ngIf="canEditBid() && useItemWiseBid" class="text-xs text-gray-500 mt-2">
          Enter your quantity and unit rate for each item. Amount = Qty × Rate (calculated automatically). Deviations from estimated BOQ will be reported.
        </p>
      </mat-card>

      <!-- Project Documents -->
      <mat-card class="p-6" *ngIf="!bidClosed">
        <h2 class="text-lg font-semibold mb-4">Project Documents</h2>
        <p class="text-sm text-gray-600 mb-4">View and download project-related documents</p>
        <div *ngIf="data.documents?.length" class="space-y-2">
          <a *ngFor="let doc of data.documents"
             [href]="getDocumentUrl(doc.file_path)"
             target="_blank"
             class="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition">
            <mat-icon class="text-blue-600">description</mat-icon>
            <span class="flex-1 font-medium">{{ doc.document_name }}</span>
            <mat-icon class="text-gray-500">download</mat-icon>
          </a>
        </div>
        <p *ngIf="!data.documents?.length" class="text-gray-500">No documents available</p>
      </mat-card>

      <!-- Raise Query (authenticated bidders) -->
      <mat-card class="p-6" *ngIf="!bidClosed && isAuthenticated && isOrgInvited">
        <h2 class="text-lg font-semibold mb-4">Raise a Query</h2>
        <p class="text-sm text-gray-600 mb-4">Have questions about the project or BOQ? Submit your query and the project owner will respond.</p>
        <div class="flex gap-2 mb-4">
          <textarea [(ngModel)]="queryText" rows="3" placeholder="Type your query here..."
            class="flex-1 border rounded-md p-2 text-sm"></textarea>
          <button mat-raised-button color="primary" (click)="raiseQuery()" [disabled]="!queryText.trim() || submittingQuery">
            {{ submittingQuery ? 'Submitting...' : 'Submit Query' }}
          </button>
        </div>
        <div *ngIf="queries.length" class="space-y-3 mt-4 pt-4 border-t">
          <h3 class="font-medium text-gray-700">Your Queries</h3>
          <div *ngFor="let q of queries" class="p-3 bg-gray-50 rounded-lg">
            <p class="text-sm text-gray-800">{{ q.query_text }}</p>
            <p class="text-xs text-gray-500 mt-1">{{ q.created_at | date: 'short' }}</p>
            <div *ngIf="q.reply_text" class="mt-3 p-2 bg-green-50 rounded border-l-4 border-green-500">
              <p class="text-sm font-medium text-green-800">Reply:</p>
              <p class="text-sm text-gray-700">{{ q.reply_text }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ q.replied_at | date: 'short' }}</p>
            </div>
            <p *ngIf="!q.reply_text && q.status === 'open'" class="text-xs text-amber-600 mt-2">Awaiting response</p>
          </div>
        </div>
      </mat-card>

      <!-- Bid Form (authenticated invited organization) -->
      <mat-card class="p-6" *ngIf="!bidClosed && !bidSubmitted && isAuthenticated && isOrgInvited">
        <h2 class="text-lg font-semibold mb-4">Submit Your Bid</h2>
        <div class="mb-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Organization</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div>
              <span class="text-gray-600">Name:</span>
              <span class="font-medium ml-2">{{ bidderOrganization?.org_name || '—' }}</span>
            </div>
            <div>
              <span class="text-gray-600">Contact email:</span>
              <span class="font-medium ml-2">{{ bidderOrganization?.email || '—' }}</span>
            </div>
          </div>
        </div>
        <div class="mb-4">
          <mat-radio-group [(ngModel)]="useItemWiseBid" (change)="onBidTypeChange()">
            <mat-radio-button [value]="true">Item-wise rates (enter rate per BOQ item above)</mat-radio-button>
            <mat-radio-button [value]="false">Total amount only</mat-radio-button>
          </mat-radio-group>
        </div>
        <form [formGroup]="bidForm" (ngSubmit)="onSubmit()">
          <mat-form-field appearance="outline" class="w-full max-w-xs" *ngIf="!useItemWiseBid">
            <input matInput type="number" formControlName="bid_amount" placeholder="Enter your bid amount" min="0" step="0.01">
            <mat-error *ngIf="bidForm.get('bid_amount')?.hasError('required')">Bid amount is required</mat-error>
            <mat-error *ngIf="bidForm.get('bid_amount')?.hasError('min')">Amount must be positive</mat-error>
          </mat-form-field>
          <div *ngIf="useItemWiseBid" class="mb-4">
            <span class="text-gray-600">Total Bid Amount: </span>
            <span class="font-bold text-lg">{{ formatCurrency(bidForm.get('bid_amount')?.value) }}</span>
          </div>
          <mat-form-field appearance="outline" class="w-full block mt-4">
            <textarea matInput formControlName="remarks" rows="3" placeholder="Any additional remarks or notes..."></textarea>
          </mat-form-field>
          <div class="mt-4">
            <button mat-raised-button color="primary" type="submit" [disabled]="submitting || (useItemWiseBid ? false : bidForm.invalid)">
              {{ submitting ? 'Submitting...' : (existingBid ? 'Update Bid' : 'Submit Bid') }}
            </button>
          </div>
        </form>
      </mat-card>

      <!-- Planner: this org sent the invitation -->
      <mat-card class="p-6 bg-slate-50 border border-slate-200" *ngIf="!bidClosed && isAuthenticated && bidderRole === 'planner'">
        <div class="flex items-start gap-3">
          <mat-icon class="text-slate-600">info</mat-icon>
          <div>
            <h2 class="text-lg font-semibold text-gray-900">You sent this invitation</h2>
            <p class="text-gray-700 mt-1">Your organization issued this bidding invitation. You can review the BOQ here, but you cannot submit a bid.</p>
          </div>
        </div>
      </mat-card>

      <!-- Logged-in org that was not invited -->
      <mat-card class="p-6 bg-amber-50 border border-amber-200" *ngIf="!bidClosed && isAuthenticated && bidderViewLoaded && bidderRole === 'not_invited'">
        <div class="flex items-start gap-3">
          <mat-icon class="text-amber-700">block</mat-icon>
          <div>
            <h2 class="text-lg font-semibold text-gray-900">Your organization was not invited</h2>
            <p class="text-gray-700 mt-1">You can view this BOQ, but only invited organizations and invited emails can submit a bid.</p>
          </div>
        </div>
      </mat-card>

      <!-- Guest Bid Form (not logged in – invited email required) -->
      <mat-card class="p-6" *ngIf="!bidClosed && !bidSubmitted && !isAuthenticated">
        <h2 class="text-lg font-semibold mb-2">Submit Your Bid</h2>
        <p class="text-gray-600 mb-4">Enter the email address this invitation was sent to. Optional name and remarks are welcome.</p>
        <div class="mb-4">
          <mat-radio-group [(ngModel)]="useItemWiseBid" (change)="onBidTypeChange()">
            <mat-radio-button [value]="true">Item-wise rates (enter rate per BOQ item above)</mat-radio-button>
            <mat-radio-button [value]="false">Total amount only</mat-radio-button>
          </mat-radio-group>
        </div>
        <form [formGroup]="guestBidForm" (ngSubmit)="onSubmitGuest()">
          <mat-form-field appearance="outline" class="w-full block">
            <input matInput type="email" formControlName="email" placeholder="Invited email address">
            <mat-error *ngIf="guestBidForm.get('email')?.hasError('required')">Email is required</mat-error>
            <mat-error *ngIf="guestBidForm.get('email')?.hasError('email')">Enter a valid email</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" class="w-full block">
            <input matInput formControlName="contact_name" placeholder="Your or company name (optional)">
          </mat-form-field>
          <mat-form-field appearance="outline" class="w-full max-w-xs" *ngIf="!useItemWiseBid">
            <input matInput type="number" formControlName="bid_amount" placeholder="Enter your bid amount" min="0" step="0.01">
            <mat-error *ngIf="guestBidForm.get('bid_amount')?.hasError('min')">Amount must be positive</mat-error>
          </mat-form-field>
          <div *ngIf="useItemWiseBid" class="mb-4">
            <span class="text-gray-600">Total Bid Amount: </span>
            <span class="font-bold text-lg">{{ formatCurrency(guestBidForm.get('bid_amount')?.value) }}</span>
          </div>
          <mat-form-field appearance="outline" class="w-full block mt-4">
            <textarea matInput formControlName="remarks" rows="3" placeholder="Any additional remarks or notes..."></textarea>
          </mat-form-field>
          <div class="mt-4 flex flex-wrap items-center gap-2">
            <button mat-raised-button color="primary" type="submit" [disabled]="submitting || guestBidForm.get('email')?.invalid || (useItemWiseBid ? false : guestBidForm.get('bid_amount')?.invalid)">
              {{ submitting ? 'Submitting...' : (existingGuestBid ? 'Update Bid' : 'Submit Bid') }}
            </button>
            <button mat-stroked-button type="button" (click)="goToLogin()">Log in</button>
            <button mat-stroked-button type="button" (click)="goToRegisterToBid()">Register</button>
          </div>
        </form>
      </mat-card>

      <!-- Success message (shown when bid was submitted before closing) -->
      <mat-card class="p-6 bg-green-50" *ngIf="bidSubmitted && !bidClosed">
        <div class="flex items-center gap-2 text-green-700">
          <mat-icon>check_circle</mat-icon>
          <h2 class="text-lg font-semibold">Bid Submitted Successfully</h2>
        </div>
        <p class="mt-2 text-gray-700">Thank you for submitting your bid. The project owner will review and get back to you.</p>
      </mat-card>
    </div>
  </div>
</div>
